import { useState } from 'react';
import classNames from 'classnames';
import { c, t } from 'ttag';

import MessageInfoIcon from '@svg/message-info.svg';
import ArrowRightIcon from '@svg/arrows-right.svg';
import { usePlatformAuthorizeInfo } from '@hook/usePlatformAuthorizeInfo';
import { Image } from '@ui/Image';
import { Button } from '@ui/Button';
import { Radio } from '@ui/Radio';
import { PromotionVariantsInfoModal } from '@components/PromotionVariantsInfoModal';
import { PhoneVerificationModalContainer } from '@containers/PhoneVerificationModal';

import s from './PromotionCard.sss';

export interface PromotionTariff {
  id: number,
  period: string,
  price: number,
  priceWithoutSale: string,
  bonusPrice?: number,
  currency: string,
}

export interface Promotion {
  title: string,
  description: string,
  icon: string,
  tariffs: PromotionTariff[],
  advantages: {
    icon?: string | null,
    description: string,
  }[]
}

interface Props {
  className?: string,
  theme?: 'default' | 'details',
  promotion: Promotion,
  isDisabled?: boolean,
  isExpand?: boolean,
  isActive?: boolean,
  onSubmit: (tariff: PromotionTariff) => void,
  onExpand?: () => void,
}

export const PromotionCard: React.FC<Props> = ({
  className,
  theme,
  promotion,
  isDisabled,
  isExpand = true,
  isActive = false,
  onSubmit,
  onExpand,
}) => {
  const [isPhoneVerificationModalOpen, setIsPhoneVerificationModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState(promotion.tariffs[0]);

  const {
    loading: platformAuthorizeInfoLoading,
    checkIsNeedPhoneVerification,
  } = usePlatformAuthorizeInfo();

  const handleSubmit = async () => {
    const isNeedPhoneVerification = await checkIsNeedPhoneVerification();

    if (isNeedPhoneVerification) {
      setIsPhoneVerificationModalOpen(true);
    } else if (selectedTariff) {
      onSubmit(selectedTariff);
    }
  };

  const {
    title,
    description,
    icon,
    tariffs,
    advantages,
  } = promotion;

  return (
    <>
      <div
        className={classNames(s.root, className, {
          [s.rootThemeDetails]: theme === 'details',
          [s.rootStateExpanded]: isExpand && isActive,
        })}
      >
        <div className={s.header}>
          <div className={s.main}>
            <Image
              className={s.icon}
              src={icon}
              width="40"
              height="40"
            />
            <div>
              <div className={s.title}>
                {title}
                <Button
                  className={s.infoButton}
                  theme="text"
                  onClick={() => setIsInfoModalOpen(true)}
                >
                  <MessageInfoIcon
                    className={s.infoIcon}
                    width="16"
                    height="16"
                  />
                </Button>
              </div>
              <p className={s.description}>
                {description}
              </p>
            </div>
          </div>

          {!!onExpand && (
          <Button
            className={s.arrowButton}
            theme="text"
            onClick={onExpand}
          >
            <ArrowRightIcon
              className={classNames(s.arrowIcon, {
                [s.arrowIconStateOpen]: isExpand,
              })}
              width="24"
              height="24"
            />
          </Button>
          )}
        </div>
        {isExpand && (
        <>
          <ul className={s.tariffList}>
            {tariffs.map((tariff) => {
              const bonusPrice = tariff.bonusPrice;

              return (
                <li
                  key={tariff.id}
                  className={s.tariffItem}
                >
                  <Button
                    theme="noTheme"
                    className={classNames(s.tariffButton, {
                      [s.tariffButtonStateChecked]: selectedTariff.id === tariff.id,
                    })}
                    onClick={() => setSelectedTariff(tariff)}
                  >
                    <div className={s.tariffHeader}>
                      {tariff.period}

                      <Radio
                        className={s.radio}
                        checked={selectedTariff.id === tariff.id}
                      />
                    </div>
                    {!!Number(bonusPrice) ? (
                      <>
                        <p className={s.tariffPrice}>
                          {tariff.price}
                          &nbsp;
                          <span className={s.tariffCurrency}>
                            {tariff.currency}
                          </span>
                        </p>
                        <p className={s.tariffBonusPrice}>
                          {t`+ ${bonusPrice} ₴ бонусних`}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className={s.tariffPrice}>
                          {tariff.price}
                          &nbsp;
                          <span className={s.tariffCurrency}>
                            {tariff.currency}
                          </span>
                        </p>
                        {tariff.price !== Number(tariff.priceWithoutSale) && (
                        <p className={s.tariffOriginalPrice}>
                          {`${tariff.priceWithoutSale} ${tariff.currency}`}
                        </p>
                        )}
                      </>
                    )}
                  </Button>
                </li>
              );
            })}
          </ul>
          <ul className={s.benefitsList}>
            {advantages?.map((advantage, i) => (
              <li
                key={i}
                className={s.benefitsItem}
              >
                <p dangerouslySetInnerHTML={{ __html: advantage.description }} />
              </li>
            ))}
          </ul>

          <Button
            className={s.button}
            disabled={isDisabled || platformAuthorizeInfoLoading}
            onClick={handleSubmit}
          >
            {c('PromotionCard').t`Оплатити · ${selectedTariff.price} ${selectedTariff.currency}`}
          </Button>
        </>
        )}
      </div>
      <PromotionVariantsInfoModal
        isOpen={isInfoModalOpen}
        onRequestClose={() => setIsInfoModalOpen(false)}
      />

      <PhoneVerificationModalContainer
        isOpen={isPhoneVerificationModalOpen}
        onClose={() => setIsPhoneVerificationModalOpen(false)}
        description={t`Щоб просунути оголошення, верифікуйте номер телефону`}
        onVerify={handleSubmit}
      />
    </>
  );
};
